import { Fragment, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { displayDateTime, displayAssociationTime } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { words, type SyncRun } from './detailTypes'

const counts = ['fetched', 'created', 'updated', 'unchanged', 'unmatched', 'errored'] as const
function duration(run: SyncRun) {
  if (!run.finished_at) return '—'
  const seconds = Math.round(
    (new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()) / 1000,
  )
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}
function RunStatus({ run }: { run: SyncRun }) {
  const variant =
    run.status === 'failed' ? 'destructive' : run.status === 'partial' ? 'warning' : 'neutral'
  return (
    <div>
      <Badge variant={variant}>{words(run.status)}</Badge>
      {run.failure_kind && (
        <p className="text-caption text-muted-foreground">{words(run.failure_kind)}</p>
      )}
    </div>
  )
}
function RunRow({ run }: { run: SyncRun }) {
  const [expanded, setExpanded] = useState(false)
  const Chevron = expanded ? ChevronDown : ChevronRight
  return (
    <Fragment>
      <TableRow className="cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <TableCell className="w-9 py-0">
          <Button
            variant="ghost"
            size="icon"
            aria-label={`${expanded ? 'Hide' : 'Show'} run log ${run.id}`}
            aria-expanded={expanded}
            onClick={(event) => {
              event.stopPropagation()
              setExpanded(!expanded)
            }}
          >
            <Chevron className="size-4" strokeWidth={1.5} />
          </Button>
        </TableCell>
        <TableCell>
          <time dateTime={run.started_at} title={displayDateTime(run.started_at)}>
            {displayAssociationTime(run.started_at)}
          </time>
        </TableCell>
        <TableCell>{words(run.trigger)}</TableCell>
        <TableCell>
          <RunStatus run={run} />
        </TableCell>
        {counts.map((count) => (
          <TableCell
            key={count}
            className={cn('text-right tabular-nums', run[count] === 0 && 'text-muted-foreground')}
          >
            {run[count]}
          </TableCell>
        ))}
        <TableCell className="text-right tabular-nums">{duration(run)}</TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={11} className="bg-muted/40">
            <pre className="whitespace-pre-wrap font-mono text-caption text-muted-foreground">
              {run.log || 'No log entries'}
            </pre>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  )
}
export function ConnectionRuns({ runs }: { runs: SyncRun[] }) {
  if (!runs.length) return <EmptyState variant="quiet" title="No runs" />
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead />
          <TableHead>Started</TableHead>
          <TableHead>Trigger</TableHead>
          <TableHead>Status</TableHead>
          {counts.map((count) => (
            <TableHead key={count} className="text-right">
              {words(count)}
            </TableHead>
          ))}
          <TableHead className="text-right">Duration</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {runs.map((run) => (
          <RunRow key={run.id} run={run} />
        ))}
      </TableBody>
    </Table>
  )
}
